import { Alert, DatePicker, Form, Modal, notification } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { mapStarError } from 'pages/star-shared/star-error';
import { formatNumber } from 'pages/star-shared/utils';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type RangeValue = [Dayjs | null, Dayjs | null] | null;

// Chấm lại KC ngày cho một khoảng ngày: POST /stars/admin/kc/evaluate { from, to }.
const KcEvaluateModal = ({ open, onClose, onSuccess }: Props) => {
  const [form] = Form.useForm<{ range: RangeValue }>();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ range: [dayjs().subtract(7, 'day'), dayjs()] });
      setResult(null);
    }
  }, [open, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: { range: RangeValue }) => {
    const from = values.range?.[0];
    const to = values.range?.[1];
    if (!from || !to) return;
    setLoading(true);
    try {
      const res: any = await AxiosUtils.post(API.starsKcEvaluate, {
        from: from.format('YYYY-MM-DD'),
        to: to.format('YYYY-MM-DD'),
      });
      setResult(res?.data || {});
      notification.success({ message: 'Đã chấm lại.' });
      onSuccess();
    } catch (err) {
      notification.error({ message: mapStarError(err) });
    } finally {
      setLoading(false);
    }
  };

  const summary =
    result && typeof result === 'object'
      ? [
          typeof result.evaluated === 'number' ? `đã xét ${formatNumber(result.evaluated)} dòng` : null,
          typeof result.rewarded === 'number' ? `thưởng ${formatNumber(result.rewarded)} lượt` : null,
          typeof result.stars === 'number' ? `chênh lệch ${formatNumber(result.stars)} ⭐` : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : '';

  return (
    <Modal
      title="Chấm lại KC theo khoảng ngày"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={520}
    >
      <Form form={form} layout="vertical" onFinish={onFinish} className="custom-form">
        <Form.Item
          label="Khoảng ngày"
          name="range"
          rules={[
            {
              validator: (_, value: RangeValue) => {
                if (!value?.[0] || !value?.[1]) return Promise.reject(new Error('Chọn khoảng ngày.'));
                if (value[1].diff(value[0], 'day') > 62) {
                  return Promise.reject(new Error('Tối đa 62 ngày mỗi lần.'));
                }
                return Promise.resolve();
              },
            },
          ]}
          extra="Chỉ cộng/trừ phần chênh lệch so với Sao đã cấp; chạy lại không cộng trùng."
        >
          <DatePicker.RangePicker format="DD/MM/YYYY" disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
        </Form.Item>
        {result && (
          <Alert type="success" showIcon style={{ marginBottom: 16 }} message={summary || 'Đã chấm lại.'} />
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Đóng" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton text="Chấm lại" bgColor="primary-orange" htmlType="submit" loading={loading} />
        </div>
      </Form>
    </Modal>
  );
};

export default KcEvaluateModal;
